import React, { useEffect } from "react";
import {
  Col,
  Row,
  UiCenter,
  UiIcon,
  UiImage,
  UiLink,
  UiPrimaryButton,
  UiText,
  useBreakpointValue,
} from "~/components/elements";
import { PageLayout } from "~/components/layouts/PageLayout";
import { appConfig } from "~/config";
import { BETTERSHOT_MAILTO_ADDRESS, PAGES } from "~/constants";
import { useAppDispatch, useRequestState } from "~/hooks";
import { createPaymentSession } from "~/redux/dashboards/thunk";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import paymentFailed from "~/assets/img/v2/packages/payment_failed.png";
import { MdArrowForwardIos } from "react-icons/md";
import { requestOK } from "redux-thunk-kit";
import { unwrapResult } from "@reduxjs/toolkit";
import { AppError } from "ry-error-wrapper";
import { useSearchParams } from "~/utils/helpers/react-router-helper";
import { useHistory } from "react-router-dom";
import { setDidRedirect } from "~/redux/dashboards/slice";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { PAYMENT_TYPE_JOB, PAYMENT_TYPE_UPSELL } from "~/constants/payment";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
};

declare const window: WindowWithDataLayer;

export default function PaymentFailedPage() {
  const sendDataToGTM = useGTMDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const packageKey = searchParams.get("package");
  const jobId = searchParams.get("job_id");
  const billingType = searchParams.get("billing_type");
  const quantity = searchParams.get("quantity");

  const params = new URLSearchParams();

  [
    ["session_id", "{CHECKOUT_SESSION_ID}"],
    ["package", packageKey],
    ["job_id", jobId],
    ["billing_type", billingType],
    ["quantity", quantity],
  ].forEach(([key, value]) => {
    if (key && value) params.append(key, value);
  });

  const queryString = params.toString();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { loading: loadingCreateSession } = useRequestState(createPaymentSession);
  const vrTitle = useBreakpointValue<any>({ base: "headline3", sm: "headline2" });

  useEffect(() => {
    if (appConfig.stage === "production") {
      sendDataToGTM({
        event: "pageview",
        page: {
          title: "payment-cancelled",
        },
      });
    }

    dispatch(setDidRedirect(true));
  }, [dispatch, sendDataToGTM]);

  const handleTryAgain = async () => {
    if (!packageKey) {
      // eslint-disable-next-line no-alert
      alert("Something wrong, please try to start again");
      return null;
    }
    const sessionType = billingType ?? PAYMENT_TYPE_JOB;
    const resAction = await dispatch(
      createPaymentSession({
        data: {
          cancel_url: `${appConfig.url}${PAGES.PAYMENT_CANCELLED}?${queryString}`,
          success_url: `${appConfig.url}${PAGES.PAYMENT_SUCCESSFUL}?${queryString}`,
          package_key: packageKey,
          type: sessionType,
        },
      })
    );
    if (requestOK(resAction, createPaymentSession)) {
      const results = unwrapResult(resAction);
      if (results.checkout_url) {
        window.open(results.checkout_url, "_self");
      } else {
        throw new AppError("No checkout URL");
      }
    }
    return null;
  };

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot`,
        metas: [{ name: "robots", content: "noindex,nofollow" }],
      }}
      as={Col}
      flex={1}
    >
      <Col flex={1} justify="center">
        <UiCenter p={4}>
          <DashboardMainBox>
            <UiCenter flexDirection="column">
              <UiImage src={paymentFailed} w="5rem" />
              <UiText vr={vrTitle} textAlign="center" mt={3}>
                Oh no, your payment failed
              </UiText>
              <UiText vr="body1" textAlign="center" mt={3}>
                Unfortunately, your payment was unsuccessful due to an unknown issue. <br />
                Please try again or use another payment method.
              </UiText>
              <Row
                gap={{ base: 2, sm: 5 }}
                flexDirection={{ base: "column", md: "row-reverse" }}
                align="center"
                justify="center"
                mt={8}
                w="full"
              >
                <UiPrimaryButton onClick={handleTryAgain} w="full" isLoading={loadingCreateSession}>
                  Try again
                  <UiIcon hideBelow="sm" as={MdArrowForwardIos} boxSize={5} color="white" />
                </UiPrimaryButton>
                <UiPrimaryButton
                  w="full"
                  maxW={{ base: undefined, md: "16.25rem" }}
                  variant="outline"
                  borderColor="darkblue"
                  bg="transparent"
                  color="darkblue"
                  _hover={{
                    bg: "transparent",
                    color: "darkblue",
                  }}
                  onClick={() => {
                    if (jobId) {
                      const jobTabName = billingType === PAYMENT_TYPE_UPSELL ? "tab_upsell" : "tab_subscription";
                      history.push(`${PAGES.RESULT_DETAIL}/${jobId}`, {
                        tabName: jobTabName,
                      });
                    } else {
                      history.push(PAGES.SELECT_PACKAGES);
                    }
                  }}
                >
                  <UiText vr="title1" color="darkblue">
                    Back to package selection
                  </UiText>
                </UiPrimaryButton>
              </Row>
              <UiLink href={BETTERSHOT_MAILTO_ADDRESS} fontWeight="700" mt={3} color="navy">
                Need help? Send us an email.
              </UiLink>
            </UiCenter>
          </DashboardMainBox>
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
